/** Screen 6: statistics per set. */
import { el, page } from '../dom';
import { getSet } from '../../core/storage';
import { boxHistogram, setProgress } from '../../core/leitner';
import { hasGroups, listGroups } from '../../core/groups';
import { BOX_COUNT, type Card } from '../../core/types';

const BOX_LABELS = ['Neu', 'Geübt', 'Sitzt fast', 'Gut', 'Gelernt'];

function accuracy(card: Card): number {
  return card.asked === 0 ? 0 : card.correct / card.asked;
}

export function statsScreen(context: { params: string[] }): HTMLElement {
  const set = getSet(context.params[0]);

  if (!set) {
    return page({
      title: 'Statistik',
      back: '/sets',
      children: [el('div', { class: 'card' }, [el('p', { text: 'Dieses Set gibt es nicht mehr.' })])],
    });
  }

  const histogram = boxHistogram(set);
  const maxCount = Math.max(1, ...histogram);
  const totalAsked = set.cards.reduce((sum, card) => sum + card.asked, 0);
  const totalCorrect = set.cards.reduce((sum, card) => sum + card.correct, 0);
  const hitRate = totalAsked === 0 ? 0 : Math.round((totalCorrect / totalAsked) * 100);

  const hardest = set.cards
    .filter((card) => card.asked >= 2)
    .sort((a, b) => accuracy(a) - accuracy(b) || b.asked - a.asked)
    .slice(0, 10);

  const bars = histogram.map((count, index) =>
    el('div', { class: 'bar-col' }, [
      el('div', { class: 'bar-track' }, [
        el('div', {
          class: 'bar-fill',
          style: `height:${Math.round((count / maxCount) * 100)}%`,
        }),
      ]),
      el('span', { class: 'bar-count', text: String(count) }),
      el('span', { class: 'bar-label', text: BOX_LABELS[index] ?? `Fach ${index + 1}` }),
    ]),
  );

  return page({
    title: 'Statistik',
    back: `/learn/${set.id}`,
    children: [
      el('div', { class: 'card' }, [
        el('h2', { class: 'section-title', text: set.name }),
        el('div', { class: 'stat-grid' }, [
          el('div', { class: 'stat' }, [
            el('span', { class: 'stat-value', text: String(set.cards.length) }),
            el('span', { class: 'stat-label', text: 'Wörter' }),
          ]),
          el('div', { class: 'stat' }, [
            el('span', { class: 'stat-value', text: `${Math.round(setProgress(set) * 100)}%` }),
            el('span', { class: 'stat-label', text: 'gelernt' }),
          ]),
          el('div', { class: 'stat' }, [
            el('span', { class: 'stat-value', text: String(totalAsked) }),
            el('span', { class: 'stat-label', text: 'Abfragen' }),
          ]),
          el('div', { class: 'stat' }, [
            el('span', { class: 'stat-value', text: `${hitRate}%` }),
            el('span', { class: 'stat-label', text: 'richtig' }),
          ]),
        ]),
      ]),
      el('div', { class: 'card' }, [
        el('h2', { class: 'section-title', text: `Wörter pro Fach (1 bis ${BOX_COUNT})` }),
        el('div', { class: 'bars' }, bars),
      ]),
      hasGroups(set)
        ? el('div', { class: 'card' }, [
            el('h2', { class: 'section-title', text: 'Lernziele' }),
            el(
              'ul',
              { class: 'goal-list' },
              listGroups(set).map((group) =>
                el('li', { class: 'goal' }, [
                  el('div', { class: 'goal-head' }, [
                    el('span', { class: 'goal-name', text: group.name || 'Ohne Lernziel' }),
                    el('span', {
                      class: 'goal-meta',
                      text: `${group.cards.length} Wörter · ${Math.round(group.progress * 100)}%`,
                    }),
                  ]),
                  el('div', { class: 'progress' }, [
                    el('div', {
                      class: 'progress-fill',
                      style: `width:${Math.round(group.progress * 100)}%`,
                    }),
                  ]),
                ]),
              ),
            ),
          ])
        : null,
      hardest.length > 0
        ? el('div', { class: 'card' }, [
            el('h2', { class: 'section-title', text: 'Schwierigste Wörter' }),
            el(
              'ul',
              { class: 'mistake-list' },
              hardest.map((card) =>
                el('li', { class: 'mistake' }, [
                  el('span', { class: 'mistake-prompt', text: card.native }),
                  el('span', { class: 'preview-arrow', text: '→' }),
                  el('span', { class: 'mistake-answer', text: card.foreign }),
                  el('span', {
                    class: 'mistake-rate',
                    text: `${card.correct}/${card.asked}`,
                  }),
                ]),
              ),
            ),
          ])
        : el('div', { class: 'card' }, [
            el('p', { class: 'hint', text: 'Übe ein paar Runden, dann erscheinen hier die schwierigsten Wörter.' }),
          ]),
    ],
  });
}
